//React
import React from 'react';

// Futurelab
import { RootState } from '../../reducers';
import { useSelector } from 'react-redux';
import { useLazyGetUserInfoQuery } from '../../store/api/userApi';
import { isSameUserId, toUserId } from '../../utilities/UserId';

// UI
import { Brain, BriefcaseBusiness, ShieldCheck, Star } from 'lucide-react';
import { Button } from '../ui/button';
import {
	Dialog,
	DialogContent,
	DialogDescription,
	DialogFooter,
	DialogHeader,
	DialogTitle,
} from '../ui/dialog';
import { Tooltip, TooltipContent, TooltipTrigger } from '../ui/tooltip';

// 3rd Party
import moment from 'moment';

export interface ProfilePreviewData {
	id: number;
	email: string;
	firstname: string;
	lastname: string;
	profile_intro: string;
	profile_photo: string;
	created_at: string;
	become_a_mentor: boolean;
	user_skills: string;
	skills: [];
	industries: [];
	hex_id: string;
	user_type: number;
}

function UserDetails({ displayUserDetails, setDisplayUserDetails, userProfileDetails, photoUrl }: any) {
	const chatInFocus = useSelector((state: RootState) => state.chatInFocus);
	const loggedInUserDetails = useSelector((state: RootState) => state.loggedInUserDetails);
	const [profilePreview, setProfilePreview] = React.useState<ProfilePreviewData>();
	const [loadUserInfo] = useLazyGetUserInfoQuery();
	const handleClose = () => setDisplayUserDetails(!displayUserDetails);

	React.useEffect(() => {
		const loggedInUserId = toUserId(loggedInUserDetails.id);
		const recipientDetails = chatInFocus?.participants.find(
			(participant) => !isSameUserId(participant.userId, loggedInUserId)
		);
		const recipientUserId = recipientDetails?.userId;
		setProfilePreview(undefined);
		if (recipientUserId) {
			loadUserInfo({ userId: recipientUserId })
				.unwrap()
				.then((user) => setProfilePreview(user as ProfilePreviewData))
				.catch((error) => console.warn('Unable to load user profile:', error));
		}
	}, [chatInFocus, loadUserInfo, loggedInUserDetails.id]);

	const userSkills = profilePreview?.skills.map(({ name }, index) => {
		if (index <= 1) {
			return name + (index < profilePreview?.skills?.length - 1 ? ', ' : '');
		}
	});

	const userSkillsInToolTips = profilePreview?.skills.map(({ name }, index) => {
		if (index >= 2) {
			return name + (index < profilePreview?.skills?.length - 1 ? ', ' : '');
		}
	});

	const goToUserProfile = () => {
		window.open(
			`${process.env.REACT_APP_B2C_BASE_URL}mentor_profile/${profilePreview?.firstname}/${profilePreview?.hex_id}`,
			'_blank'
		);
	};

	return (
		<Dialog open={displayUserDetails} onOpenChange={(open) => !open && handleClose()}>
			<DialogContent className="max-h-[calc(100dvh-2rem)] w-[calc(100%-2rem)] min-w-0 overflow-x-hidden overflow-y-auto p-4 sm:max-w-sm sm:p-5" showCloseButton>
				<DialogHeader className="sr-only">
					<DialogTitle>User details</DialogTitle>
					<DialogDescription>User profile information and mentoring details.</DialogDescription>
				</DialogHeader>
				<div className="grid min-w-0 grid-cols-[minmax(0,1fr)_minmax(0,2fr)] items-center gap-3 sm:gap-4">
					<div className="min-w-0">
					{!!userProfileDetails?.profile_photo ? (
						<img
							className="user-picture h-auto max-w-full"
							src={`${photoUrl}/${userProfileDetails?.id}/${userProfileDetails?.profile_photo}`}
							alt=""
						/>
					) : (
						<img className="user-picture h-auto max-w-full" src="../../../default-profile-pic.png" alt="" />
					)}
					</div>
					<div className="username min-w-0">
					<span className="break-words">
						{`${profilePreview?.firstname} ${profilePreview?.lastname ?? ''}`}
						<Star className="mentor-star-icon inline size-4" fill="currentColor" />
						5.0
					</span>
					<div className="user-profession-container mt-2 flex gap-2">
						<BriefcaseBusiness className="size-4 shrink-0 text-muted-foreground" />
						<div className="user-profession min-w-0 break-words">
							<span>{profilePreview?.profile_intro}</span>
						</div>
					</div>
					</div>
				</div>
				<div className="user-description mt-5 min-w-0 space-y-3">
					<div className="grid grid-cols-[1.25rem_1fr] gap-2">
						<ShieldCheck className="size-4 text-muted-foreground" />
						<div className="min-w-0 break-words">
						<span className="user-mentoring-since">
							{profilePreview?.user_type === 1
								? `Mentor since ${moment(profilePreview?.created_at).format('YYYY')} (${moment(
										profilePreview.created_at
								  ).fromNow()})`
								: `Mentee`}
						</span>
						</div>
					</div>
					<div className="user-skill grid grid-cols-[1.25rem_1fr] gap-2">
						<Brain className="size-4 text-muted-foreground" />
						<div className="min-w-0 break-words">
						<span>{userSkills}</span>
						<Tooltip>
							<TooltipTrigger>
							<span className="skill-tooltip-counter">
								{userSkills?.length ? `+${userSkills.length - 2}` : `Skills have not been added.`}
							</span>
							</TooltipTrigger>
							<TooltipContent>{userSkillsInToolTips}</TooltipContent>
						</Tooltip>
						</div>
					</div>
				</div>
			{profilePreview?.user_type === 1 ? (
				<DialogFooter className="chat-popup-button-group mt-5">
						<Button variant="outline" onClick={goToUserProfile}>
							See Profile
						</Button>
						<Button onClick={goToUserProfile}>
							Book a Session
						</Button>
				</DialogFooter>
			) : (
				''
			)}
			</DialogContent>
		</Dialog>
	);
}

export default UserDetails;
